import { useMemo, useState } from "react";
import { AdminTableSkeleton } from "../../components/admin/AdminTableSkeleton";
import { ModalShell } from "../../components/ModalShell";
import { useAdminUsers, type AdminUser } from "../../hooks/useAdminUsers";
import {
  type AdminFlag,
  type AdminFlagOverride,
  useAdminFeatureFlags,
} from "../../hooks/useAdminFeatureFlags";
import { formatDateTime } from "../../utils/formatDateTime";
import styles from "../AdminInstances.module.css";
import flagStyles from "./AdminFeatureFlags.module.css";
import { AdminPageIntro } from "../../components/admin/AdminPageIntro";

type GlobalChoice = "inherit" | "on" | "off";

const choiceForFlag = (flag: AdminFlag): GlobalChoice => {
  if (flag.globalOverride === null) return "inherit";
  return flag.globalOverride ? "on" : "off";
};

const effectiveValue = (flag: AdminFlag): boolean => {
  if (flag.globalOverride !== null) return flag.globalOverride;
  return flag.default;
};

export const AdminFeatureFlags = (): JSX.Element => {
  const {
    status,
    flags,
    errorMessage,
    setGlobalOverride,
    clearGlobalOverride,
    listOverrides,
    setUserOverride,
    clearUserOverride,
  } = useAdminFeatureFlags();
  const { users } = useAdminUsers();

  const [busy, setBusy] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [openFlag, setOpenFlag] = useState<AdminFlag | null>(null);
  const [overrides, setOverrides] = useState<ReadonlyArray<AdminFlagOverride>>([]);

  const handleChoice = async (flag: AdminFlag, choice: GlobalChoice): Promise<void> => {
    setBusy(flag.key);
    setFlash(null);
    try {
      if (choice === "inherit") {
        await clearGlobalOverride(flag.key);
        setFlash(`Cleared global override on ${flag.key}.`);
      } else {
        await setGlobalOverride(flag.key, choice === "on");
        setFlash(`Set ${flag.key} = ${choice} globally.`);
      }
    } catch (err) {
      setFlash(`Update failed: ${err instanceof Error ? err.message : "unknown"}`);
    } finally {
      setBusy(null);
    }
  };

  const handleOpenOverrides = async (flag: AdminFlag): Promise<void> => {
    setBusy(flag.key);
    try {
      const rows = await listOverrides(flag.key);
      setOverrides(rows);
      setOpenFlag(flag);
    } catch (err) {
      setFlash(`Load overrides failed: ${err instanceof Error ? err.message : "unknown"}`);
    } finally {
      setBusy(null);
    }
  };

  const handleClearUserOverride = async (key: string, email: string): Promise<void> => {
    try {
      await clearUserOverride(key, email);
      setOverrides((prev) => prev.filter((o) => o.email !== email));
      setFlash(`Cleared ${key} override for ${email}.`);
    } catch (err) {
      setFlash(`Clear failed: ${err instanceof Error ? err.message : "unknown"}`);
    }
  };

  const handleAddUserOverride = async (
    key: string,
    email: string,
    enabled: boolean,
  ): Promise<{ ok: true } | { ok: false; message: string }> => {
    try {
      await setUserOverride(key, email, enabled);
      const fresh = await listOverrides(key);
      setOverrides(fresh);
      setFlash(`Set ${key} = ${enabled ? "on" : "off"} for ${email}.`);
      return { ok: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : "unknown";
      return { ok: false, message };
    }
  };

  return (
    <div className={styles.page}>
      <AdminPageIntro title="Feature flags">
        Flags are defined in code (<code>FeatureFlag.kt</code>). The global toggle overrides the code default for everyone; <em>Overrides</em> sets a flag per user.
      </AdminPageIntro>

      {flash && <div className={styles.flash}>{flash}</div>}

      <section className={styles.listSection}>
        {status === "loading" && (
          <AdminTableSkeleton
            headers={["Flag", "Global", "Default", "Effective", "Overrides", ""]}
            tableClassName={styles.table ?? ""}
          />
        )}
        {status === "error" && (
          <div className={styles.empty}>{errorMessage ?? "Failed to load."}</div>
        )}
        {status === "ready" && flags.length === 0 && (
          <div className={styles.empty}>No flags registered.</div>
        )}
        {status === "ready" && flags.length > 0 && (
          <table className={`${styles.table} ${flagStyles.flagTable}`}>
            <thead>
              <tr>
                <th>Flag</th>
                <th>Global</th>
                <th className={flagStyles.center}>Default</th>
                <th className={flagStyles.center}>Effective</th>
                <th className={flagStyles.center}>Overrides</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {flags.map((flag) => {
                const choice = choiceForFlag(flag);
                const isBusy = busy === flag.key;
                return (
                  <tr key={flag.key}>
                    <td className={flagStyles.flagCell}>
                      <code>{flag.key}</code>
                      <span className={flagStyles.descriptionText} title={flag.description}>
                        {flag.description}
                      </span>
                    </td>
                    <td>
                      <select
                        className={flagStyles.choice}
                        value={choice}
                        disabled={isBusy}
                        onChange={(e) => handleChoice(flag, e.target.value as GlobalChoice)}
                      >
                        <option value="inherit">inherit ({flag.default ? "on" : "off"})</option>
                        <option value="on">on</option>
                        <option value="off">off</option>
                      </select>
                    </td>
                    <td className={flagStyles.center}>{flag.default ? "on" : "off"}</td>
                    <td className={flagStyles.center}>
                      <strong>{effectiveValue(flag) ? "on" : "off"}</strong>
                    </td>
                    <td className={flagStyles.center}>{flag.userOverrideCount}</td>
                    <td className={flagStyles.actions}>
                      <button
                        type="button"
                        className={flagStyles.overridesBtn}
                        onClick={() => void handleOpenOverrides(flag)}
                        disabled={isBusy}
                      >
                        Overrides
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {openFlag && (
        <OverridesDialog
          flag={openFlag}
          overrides={overrides}
          users={users}
          onClose={() => setOpenFlag(null)}
          onClear={(email) => void handleClearUserOverride(openFlag.key, email)}
          onAdd={(email, enabled) => handleAddUserOverride(openFlag.key, email, enabled)}
        />
      )}
    </div>
  );
};

type OverridesDialogProps = {
  flag: AdminFlag;
  overrides: ReadonlyArray<AdminFlagOverride>;
  users: ReadonlyArray<AdminUser>;
  onClose: () => void;
  onClear: (email: string) => void;
  onAdd: (email: string, enabled: boolean) => Promise<{ ok: true } | { ok: false; message: string }>;
};

const userLabel = (u: AdminUser): string => {
  const tag =
    u.status === "active" ? "" :
    u.status === "invited" ? " · invited" :
    u.status === "revoked" ? " · revoked" :
    " · inactive";
  return `${u.email}${tag}`;
};

const OverridesDialog = ({ flag, overrides, users, onClose, onClear, onAdd }: OverridesDialogProps): JSX.Element => {
  const [addEmail, setAddEmail] = useState<string>("");
  const [addValue, setAddValue] = useState<"on" | "off">("on");
  const [addBusy, setAddBusy] = useState<boolean>(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Hide users that already have an override; cuts the dropdown down to
  // the people you can actually add right now. Sorted alphabetically
  // with revoked / inactive pushed to the bottom.
  const overriddenEmails = useMemo(
    () => new Set(overrides.map((o) => o.email.toLowerCase())),
    [overrides],
  );

  const selectable = useMemo<ReadonlyArray<AdminUser>>(() => {
    const statusRank: Record<AdminUser["status"], number> = {
      active: 0,
      invited: 1,
      inactive: 2,
      revoked: 3,
    };
    return users
      .filter((u) => !overriddenEmails.has(u.email.toLowerCase()))
      .slice()
      .sort((a, b) => {
        const r = statusRank[a.status] - statusRank[b.status];
        return r !== 0 ? r : a.email.localeCompare(b.email);
      });
  }, [users, overriddenEmails]);

  const handleAdd = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!addEmail) {
      setAddError("Pick a user.");
      return;
    }
    setAddBusy(true);
    setAddError(null);
    const result = await onAdd(addEmail.toLowerCase(), addValue === "on");
    setAddBusy(false);
    if (result.ok) {
      setAddEmail("");
      setAddValue("on");
    } else {
      setAddError(result.message);
    }
  };

  const titleId = `flag-overrides-title-${flag.key}`;

  return (
    <ModalShell density="compact" onClose={onClose} titleId={titleId}>
      <header className={flagStyles.dialogHeader}>
          <h3 className={flagStyles.dialogTitle} id={titleId}>
            User overrides — <code>{flag.key}</code>
          </h3>
          <button type="button" className={flagStyles.closeBtn} onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <form className={flagStyles.addOverrideRow} onSubmit={handleAdd}>
          <select
            value={addEmail}
            disabled={addBusy || selectable.length === 0}
            onChange={(e) => {
              setAddEmail(e.target.value);
              setAddError(null);
            }}
            aria-label="User to override"
          >
            <option value="">
              {selectable.length === 0 ? "All users overridden" : "Select user…"}
            </option>
            {selectable.map((u) => (
              <option key={u.email} value={u.email}>
                {userLabel(u)}
              </option>
            ))}
          </select>
          <select
            value={addValue}
            disabled={addBusy}
            onChange={(e) => setAddValue(e.target.value as "on" | "off")}
            aria-label="Override value"
          >
            <option value="on">on</option>
            <option value="off">off</option>
          </select>
          <button
            type="submit"
            className={styles.primaryBtn}
            disabled={addBusy || addEmail === ""}
            title={addEmail === "" ? "Pick a user" : undefined}
          >
            {addBusy ? "Adding…" : "Add override"}
          </button>
        </form>
        {addError && <p className={flagStyles.errorBanner}>{addError}</p>}

        {overrides.length === 0 ? (
          <p className={flagStyles.empty}>No per-user overrides yet.</p>
        ) : (
          <table className={flagStyles.overridesTable}>
            <thead>
              <tr>
                <th>Email</th>
                <th>Value</th>
                <th>Updated</th>
                <th>By</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {overrides.map((row) => (
                <tr key={row.email}>
                  <td>{row.email}</td>
                  <td>{row.enabled ? "on" : "off"}</td>
                  <td className={styles.nowrap}>{formatDateTime(row.updatedAt)}</td>
                  <td>{row.updatedBy}</td>
                  <td>
                    <button
                      type="button"
                      className={flagStyles.linkBtn}
                      onClick={() => onClear(row.email)}
                    >
                      clear
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </ModalShell>
  );
};
